'use client';
import { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref, type RefObject } from 'react';
import { mentionLabel, type MentionPerson } from 'elowen-plugin-shared/composerMentions';

export type { MentionPerson };
import { Avatar } from './Avatar';
import { ConfirmDialog } from './ConfirmDialog';
import { Popover, PopoverAnchor, PopoverContent } from './shadcn/popover';
import { Command, CommandGroup, CommandItem, CommandList } from './shadcn/command';
import { normalizeText } from '../../lib/normalizeText';
import { caretRect } from '../../lib/textareaCaret';

/** The typed `@query` the picker completes: `start` is the `@`, `end` the caret. */
export interface MentionTarget { start: number; end: number; query: string }

/** A row the picker offers: an agent target or a person. */
export type MentionPick = 'agent' | MentionPerson;

/** The agent as a mention target, drawn as its own first row. */
export interface MentionAgent {
  /** The name the row shows and the token is written with. */
  label: string;
  /** The product mark beside the name. */
  src?: string;
  /** What mentioning it does while that is not obvious (chat's paused agent); shown dimmed. */
  hint?: string;
}

/** The composer's key while the picker is open; the picker takes it by returning true. */
export interface MentionKeyPress {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  preventDefault(): void;
}

export interface MentionPickerHandle {
  /** The composer's keys while the picker is open; true when the picker took the key. */
  keyDown: (event: MentionKeyPress) => boolean;
}

/** The invite confirmation's own copy: a candidate is a commitment, so it is confirmed before it is
 *  mentioned. Supplied together with {@link MentionPickerProps.onInvite} and never on its own. */
export interface MentionInviteLabels {
  title: string;
  body?: string;
  confirm: string;
  /** Shown when the invitation fails without a message of its own. */
  failed: string;
}

interface MentionPickerBaseProps {
  ref?: Ref<MentionPickerHandle>;
  /** The composer's textarea: the `@` is measured in it, and presses inside its form never dismiss the picker. */
  fieldRef: RefObject<HTMLTextAreaElement | null>;
  /** The query being typed, or null while the picker is closed. */
  target: MentionTarget | null;
  /** People the viewer can mention now: the agent row's neighbours, filtered by the query. */
  participants: readonly MentionPerson[];
  /** People who could still be invited; selectable by inserting them or, with {@link MentionInviteProps.onInvite},
   *  only once the viewer confirmed the invitation. */
  candidates?: readonly MentionPerson[];
  /** The agent target, when this surface has one. */
  agent?: MentionAgent | null;
  /** The list's accessible name ("Mention someone"). */
  placeholder: string;
  onInsert: (target: MentionTarget, pick: MentionPick) => void;
  onDismiss: () => void;
  /** The listbox and highlighted option ids for the textarea's `aria-controls`/`aria-activedescendant`. */
  onAria?: (list: string | undefined, option: string | undefined) => void;
}

/** A candidate becomes an invitation exactly when the caller handles invitations, and then it must name
 *  its confirmation: a picker that asked for a decision with no words for it would be a dead end. */
type MentionInviteProps = { onInvite: (person: MentionPerson) => Promise<void>; invite: MentionInviteLabels }
  | { onInvite?: undefined; invite?: undefined };

export type MentionPickerProps = MentionPickerBaseProps & MentionInviteProps;

const matching = (profiles: readonly MentionPerson[], query: string): MentionPerson[] => {
  const needle = normalizeText(query);
  return profiles.filter((profile) => normalizeText(`${profile.name} ${profile.username}`).includes(needle));
};

const pickId = (pick: MentionPick): number | 'agent' => (pick === 'agent' ? pick : pick.id);

/** cmdk needs a non-empty string value per row. */
const optionValue = (id: number | 'agent'): string => `mention:${id}`;

/** The agent's row: its mark and name, and while mentioning it does something notable, what. */
function AgentRow({ agent, onSelect }: { agent: MentionAgent; onSelect: () => void }) {
  return (
    <CommandItem value={optionValue('agent')} onSelect={onSelect}>
      <span aria-hidden className="flex shrink-0">
        <Avatar user={{ id: 0, username: agent.label }} src={agent.src} size={20} />
      </span>
      <span className="min-w-0 truncate">{agent.label}</span>
      {agent.hint ? <span className="min-w-0 shrink-2 truncate text-meta text-muted-foreground">{agent.hint}</span> : null}
    </CommandItem>
  );
}

function MentionRow({ profile, onSelect }: { profile: MentionPerson; onSelect: () => void }) {
  return (
    <CommandItem value={optionValue(profile.id)} onSelect={onSelect}>
      <span aria-hidden className="flex shrink-0">
        <Avatar user={{ id: profile.id, username: profile.username, name: profile.name, avatar: profile.avatar ?? undefined }} size={20} />
      </span>
      <span className="min-w-0 truncate">{mentionLabel(profile)}</span>
      <span className="min-w-0 shrink-2 truncate text-meta text-muted-foreground">@{profile.username}</span>
    </CommandItem>
  );
}

/** A person's identity in the invitation confirmation: the avatar, the name and the handle. */
function InviteRow({ profile }: { profile: MentionPerson }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar user={{ id: profile.id, username: profile.username, name: profile.name, avatar: profile.avatar ?? undefined }} size={24} />
      <span className="min-w-0 truncate">{mentionLabel(profile)}</span>
      <span className="min-w-0 truncate text-meta text-muted-foreground">@{profile.username}</span>
    </div>
  );
}

/** A textarea's `@` completion: a compact Popover anchored to the typed `@` itself (measured in the field
 *  by {@link caretRect}), above it unless there is no room, then below, and kept inside the viewport. Its
 *  Command list holds the agent (when the surface offers one) and the people the viewer may mention and,
 *  after a quiet divider, the people who could still be invited. The field keeps focus and the caret; it
 *  hands its keys to {@link MentionPickerHandle.keyDown} with the slash menu's grammar (arrows move, Enter
 *  or Tab inserts, Escape closes and leaves the typed `@`). A candidate is an invitation: the viewer
 *  confirms it first (see {@link MentionPickerProps.onInvite}) and the token is inserted only once the
 *  invitation succeeded. Nothing here reads a conversation, a profile cache or an API: every row and every
 *  word arrives as a prop, so a plugin's own editor and the chat composer share one picker. */
export function MentionPicker({ ref, fieldRef, target, participants, candidates, agent, placeholder, onInsert, onInvite, invite, onDismiss, onAria }: MentionPickerProps) {
  const agentShown = agent != null && target !== null && normalizeText(agent.label).includes(normalizeText(target.query));
  const members: MentionPick[] = [...(agentShown ? ['agent' as const] : []), ...(target ? matching(participants, target.query) : [])];
  const others = target ? matching(candidates ?? [], target.query) : [];
  const selectable: MentionPick[] = [...members, ...others];
  const open = target !== null && selectable.length > 0;
  // The highlight belongs to one `@`: a new query starts on its first row.
  const [highlight, setHighlight] = useState<{ start: number; id: number | 'agent' } | null>(null);
  const active = selectable.find((pick) => target && highlight?.start === target.start && pickId(pick) === highlight.id) ?? selectable[0];
  // A new anchor object makes Radix measure again, so the list follows the `@` when the growing word wraps
  // and when the draft scrolls inside the field: the positioning watches the page's scrolling and the list's
  // own ancestors, never the textarea's inner scroll.
  const start = target?.start;
  const end = target?.end;
  const [scrolls, setScrolls] = useState(0);
  useEffect(() => {
    if (start === undefined) return;
    const field = fieldRef.current!;
    const moved = (): void => setScrolls((count) => count + 1);
    field.addEventListener('scroll', moved, { passive: true });
    return () => field.removeEventListener('scroll', moved);
  }, [fieldRef, start]);
  const anchor = useMemo(() => (start === undefined || end === undefined ? null : {
    current: {
      // Radix may measure the anchor after the field is gone (an unmount mid-positioning, a route change):
      // there is nothing left to measure against, and a zero rectangle places nothing rather than throwing.
      getBoundingClientRect: () => { const field = fieldRef.current; return field ? caretRect(field, start) : new DOMRect(); },
    },
    // Not read by Radix: it ties this object to the field's scroll position, so a scroll makes a new one.
    scrolls,
  }), [fieldRef, start, end, scrolls]);
  const [inviting, setInviting] = useState<{ target: MentionTarget; profile: MentionPerson } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const pick = (choice: MentionPick): void => {
    if (!target) return;
    if (choice === 'agent' || members.includes(choice)) { onInsert(target, choice); return; }
    if (!onInvite) { onInsert(target, choice); return; }
    setInviting({ target, profile: choice });
    onDismiss();
  };

  useImperativeHandle(ref, () => ({
    keyDown: (event) => {
      if (!open || !target) return false;
      if (event.key === 'Escape') { event.preventDefault(); onDismiss(); return true; }
      if (!active) return false;
      const plain = !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;
      if (plain && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = selectable[(selectable.indexOf(active) + step + selectable.length) % selectable.length]!;
        setHighlight({ start: target.start, id: pickId(next) });
        return true;
      }
      if (plain && (event.key === 'Enter' || event.key === 'Tab')) { event.preventDefault(); pick(active); return true; }
      return false;
    },
  }));

  // cmdk names its listbox and rows itself; read the ids back for the field that keeps the focus. Only a
  // change is reported: the picker renders with every keystroke, and an unchanged report would still cost
  // the composer a second commit per character.
  const reported = useRef<{ list?: string; option?: string }>({});
  useLayoutEffect(() => {
    if (!onAria) return;
    const list = listRef.current;
    const ids = open && list ? {
      list: list.id,
      option: (active ? list.querySelector(`[cmdk-item][data-value="${optionValue(pickId(active))}"]`) : null)?.id,
    } : {};
    if (ids.list === reported.current.list && ids.option === reported.current.option) return;
    reported.current = ids;
    onAria(ids.list, ids.option);
  });

  return (
    <>
      <Popover open={open} onOpenChange={(next) => { if (!next) onDismiss(); }}>
        {anchor ? <PopoverAnchor virtualRef={anchor} /> : null}
        <PopoverContent
          side="top"
          align="start"
          sideOffset={4}
          role="presentation"
          className="w-[min(--spacing(64),calc(100vw_-_--spacing(6)))] overflow-hidden p-0"
          // The field keeps the focus and the caret the whole time the picker is open.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onMouseDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => { if (event.target instanceof Node && fieldRef.current?.form?.contains(event.target)) event.preventDefault(); }}
        >
          <Command
            shouldFilter={false}
            value={active ? optionValue(pickId(active)) : ''}
            onValueChange={(value) => {
              const hovered = selectable.find((choice) => optionValue(pickId(choice)) === value);
              if (hovered && target) setHighlight({ start: target.start, id: pickId(hovered) });
            }}
          >
            <CommandList ref={listRef} label={placeholder} className="max-h-[min(--spacing(51),var(--radix-popover-content-available-height))] p-1">
              {members.length > 0 && (
                <CommandGroup className="p-0">
                  {members.map((choice) => (choice === 'agent'
                    ? <AgentRow key="agent" agent={agent!} onSelect={() => pick(choice)} />
                    : <MentionRow key={choice.id} profile={choice} onSelect={() => pick(choice)} />))}
                </CommandGroup>
              )}
              {others.length > 0 && (
                <CommandGroup className={members.length > 0 ? 'mt-1 border-t border-border p-0 pt-1' : 'p-0'}>
                  {others.map((profile) => <MentionRow key={profile.id} profile={profile} onSelect={() => pick(profile)} />)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {inviting && onInvite && invite && (
        <ConfirmDialog
          open
          title={invite.title}
          description={invite.body}
          confirmLabel={invite.confirm}
          confirmVariant="accent"
          failureMessage={(error) => error instanceof Error ? error.message : invite.failed}
          onClose={() => setInviting(null)}
          onConfirm={async () => {
            await onInvite(inviting.profile);
            onInsert(inviting.target, inviting.profile);
            setInviting(null);
          }}
        >
          <InviteRow profile={inviting.profile} />
        </ConfirmDialog>
      )}
    </>
  );
}
